// Whole-second comparison between a session's duration and its gait times.
//
// GaitDurationAllocator (useGaitAllocation) works in whole seconds: its cap is
// Math.round(elapsed_time) and it emits each gait rounded. A phone session's
// elapsed_time is a float (e.g. 1799.64 s), so comparing the raw float with the
// rounded gaits wiped every gait as soon as the user allocated the full slider
// (1800 > 1799.64). Both sides are therefore compared in whole seconds here.
// The session editor, the allocator and the declared gaits (declared-gaits.js)
// use this rule.
//
// Pure module (no '@/' import) so the unit tests can load it under Node.

export const GAIT_TIME_KEYS = ['walk_time', 'trot_time', 'canter_time', 'standing_time']

// A number, 0 for anything else (missing, non-numeric, infinite).
export const finite = (value) => {
    const number = Number(value)
    return Number.isFinite(number) ? number : 0
}

// The session sent back by a screen that does not edit the gaits (rename,
// validation, heart rate, crop): without the gait times, so a copy fetched
// before the gaits were declared cannot erase them.
export const withoutGaitTimes = (session) =>
    Object.fromEntries(Object.entries(session ?? {}).filter(([key]) => !GAIT_TIME_KEYS.includes(key)))

// Duration in whole seconds, rounded to the nearest: the allocator's totalSafe.
export const wholeSeconds = (value) => Math.max(0, Math.round(finite(value)))

// Raw sum of the gait times (missing or non-numeric gaits count as 0).
export const gaitTimeSum = (gaits, keys = GAIT_TIME_KEYS) => keys.reduce((acc, key) => acc + finite(gaits?.[key]), 0)

// True when the gaits cannot fit the duration: no duration at all, or a gait
// sum above the duration once both are rounded to whole seconds. `keys` limits
// the sum (the allocator passes its own, distances included).
export const gaitsExceedDuration = (gaits, elapsed, keys = GAIT_TIME_KEYS) => {
    const total = wholeSeconds(elapsed)
    return total === 0 || Math.round(gaitTimeSum(gaits, keys)) > total
}
